<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { UiFormBuilder, UiButton, UiText } from "@shopexperts/shared";
import type { BuilderRowConfig } from "@shopexperts/shared";
import StepPanel from "./StepPanel.vue";
import { freelancerContactInfoFormSchema, agencyContactInfoFormSchema, appServiceProviderContactInfoFormSchema, type ContactInfoFormSchema, type FreelancerContactInfoFormSchema, type AgencyContactInfoFormSchema } from "./schemas/contactInfoForm";
import { countriesShopify as countriesEn } from "@shopexperts/shared";
import { agencyRepresentativeRoleOptions } from '@/utils/roles';
import { useEmailAvailability } from './composables/useEmailAvailability';

const router = useRouter()

const storedType = localStorage.getItem('listingType')
const listingType = ref<'freelancer' | 'agency' | 'app_service_provider'>(
    (storedType === 'freelancer' || storedType === 'agency' || storedType === 'app_service_provider') ? storedType : 'freelancer'
)
const isAgencyFlow = computed(() => listingType.value === 'agency' || listingType.value === 'app_service_provider')
const isAppServiceProvider = computed(() => listingType.value === 'app_service_provider')

const COUNTRIES_OPTIONS = countriesEn.map(country => ({ label: country.name, value: country.iso2 }));

const contactInfoSchema = computed(() =>
    listingType.value === 'freelancer' ? freelancerContactInfoFormSchema
      : isAppServiceProvider.value ? appServiceProviderContactInfoFormSchema : agencyContactInfoFormSchema
);

const emailHint = computed(() => {
  if (isCheckingEmail.value) {
    return 'Checking email availability...'
  }
  return undefined
})

const rows = computed<BuilderRowConfig[]>(() => {
  if (listingType.value === 'freelancer') {
    return [
      {
        columns: [
          { type: 'text', name: 'firstName', label: 'First Name', props: { placeholder: 'First Name' } },
          { type: 'text', name: 'lastName', label: 'Last Name', props: { placeholder: 'Last Name' } },
        ]
      },
      {
        columns: [
          { type: 'text', name: 'email', label: 'Email', hint: emailHint.value, props: { placeholder: 'Enter your email' } },
        ]
      },
      {
        columns: [
          { type: 'combobox', name: 'country', label: 'Location', props: { placeholder: 'Enter your country', options: COUNTRIES_OPTIONS } },
        ]
      },
      {
        columns: [
          { type: 'url', name: 'website', label: 'Portfolio / Website URL', props: { placeholder: 'https://example.com' } },
        ]
      },
      {
        columns: [
          { type: 'url', name: 'linkedIn', label: 'LinkedIn URL', hintContent: 'Go to <a class="se-font-caption text-se-highlight underline" target="__blank" href="https://www.linkedin.com/" target="_blank">your LinkedIn profile</a> to get your profile URL.', props: { placeholder: 'https://linkedin.com' } },
        ]
      },
    ];
  } else {
    return [
      {
        columns: [
          {
            type: 'text',
            name: 'agencyName',
            label: isAppServiceProvider.value ? 'App Business Name' : 'Agency Name',
            props: { placeholder: isAppServiceProvider.value ? 'Enter app business name' : 'Enter agency name' }
          },
        ]
      },
      {
        columns: [
          { type: 'text', name: 'email', label: 'Email', hint: emailHint.value, props: { placeholder: 'Enter your email' } },
        ]
      },
      {
        columns: [
          { type: 'combobox', name: 'country', label: 'Location', props: { placeholder: 'Enter your country', options: COUNTRIES_OPTIONS } },
        ]
      },
      ...(isAppServiceProvider.value ? [] : [{
        columns: [
          {
            type: 'url' as const,
            name: 'website',
            label: 'Agency Website URL',
            props: { placeholder: 'https://example.com' },
          },
        ]
      }]),
      {
        columns: [
          {
            heading: isAppServiceProvider.value ? 'App Representative' : 'Agency Representative',
            subtitle: isAppServiceProvider.value
              ? 'The person who represents your app business and manages your app listing.'
              : 'The person who represents your agency and manages your agency listing.',
            divider: true
          },
        ]
      },
      {
        columns: [
          { type: 'text', name: 'representativeName', label: 'Representative Name', inlineHint: 'The full name of the responsible person', props: { placeholder: 'Enter representative full name' } },
        ]
      },
      {
        columns: [
          { type: 'select', name: 'representativeRole', label: 'Representative Role', props: { placeholder: 'Select representative role...', options: agencyRepresentativeRoleOptions } },
        ]
      },
      {
        columns: [
          { type: 'url', name: 'linkedIn', label: 'Representative LinkedIn', hintContent: 'Recommended for faster approval. Go to <a class="se-font-caption text-se-highlight underline" target="__blank" href="https://www.linkedin.com/" target="_blank">your LinkedIn profile</a> to get your profile URL.', props: { placeholder: 'https://linkedin.com' } },
        ]
      },
    ];
  }
});

const initialValues = ref<ContactInfoFormSchema | undefined>(undefined);
const model = ref<ContactInfoFormSchema>();
const hasLoaded = ref<boolean>(false);
const formBuilder = ref<any>(null);

// Email availability check using TanStack Query
const emailRef = computed(() => model.value?.email)
const { serverError: emailError, isChecking: isCheckingEmail } = useEmailAvailability(emailRef)

// Combine all server errors
const serverErrors = computed(() => {
  const errors: Record<string, string | undefined> = {}
  if (emailError.value) {
    errors.email = emailError.value
  }
  return errors
});

onMounted(() => {
  if (!storedType || (storedType !== 'freelancer' && storedType !== 'agency' && storedType !== 'app_service_provider')) {
    router.push('/expert/signup')
    return
  }

  const saved = localStorage.getItem('contactInfo')
  if (saved) {
    const savedData = JSON.parse(saved);
    const loadedData: Partial<FreelancerContactInfoFormSchema & AgencyContactInfoFormSchema> = {};
    
    // Only set values that are not undefined
    if ('firstName' in savedData && savedData.firstName !== undefined) loadedData.firstName = savedData.firstName || '';
    if ('lastName' in savedData && savedData.lastName !== undefined) loadedData.lastName = savedData.lastName || '';
    if ('email' in savedData && savedData.email !== undefined) loadedData.email = savedData.email || '';
    if ('country' in savedData && savedData.country !== undefined) loadedData.country = savedData.country || '';
    if ('website' in savedData && savedData.website !== undefined) loadedData.website = savedData.website;
    if ('agencyName' in savedData && savedData.agencyName !== undefined) loadedData.agencyName = savedData.agencyName || '';
    if ('linkedIn' in savedData && savedData.linkedIn !== undefined) loadedData.linkedIn = savedData.linkedIn;
    if ('representativeName' in savedData && savedData.representativeName !== undefined) loadedData.representativeName = savedData.representativeName || '';
    if ('representativeRole' in savedData && savedData.representativeRole !== undefined) loadedData.representativeRole = savedData.representativeRole || '';
    
    initialValues.value = loadedData as ContactInfoFormSchema;
    model.value = loadedData as ContactInfoFormSchema;
  }

  console.log(model.value)

  const storedErrors = localStorage.getItem('signupErrors')
  if (storedErrors) {
    const errors = JSON.parse(storedErrors)
    const mappedErrors: Record<string, string | undefined> = {}

    if (errors.firstName) mappedErrors.firstName = errors.firstName
    if (errors.lastName) mappedErrors.lastName = errors.lastName
    if (errors.email) mappedErrors.email = errors.email
    if (errors.country) mappedErrors.country = errors.country
    if (errors.linkedIn) mappedErrors.linkedIn = errors.linkedIn
    if (errors.website) mappedErrors.website = errors.website

    if (isAgencyFlow.value) {
      if (errors.agencyName) mappedErrors.agencyName = errors.agencyName
      if (errors.representativeName) mappedErrors.representativeName = errors.representativeName
      if (errors.representativeRole) mappedErrors.representativeRole = errors.representativeRole
    }

    localStorage.removeItem('signupErrors')

    setTimeout(() => {
      if (formBuilder.value && formBuilder.value.setErrors) {
        formBuilder.value.setErrors(mappedErrors)

        // Auto-clear these temporary errors after 3 seconds
        // But preserve any server errors (like email availability)
        setTimeout(() => {
          if (formBuilder.value && formBuilder.value.setFieldError) {
            // Clear only the specific fields that were set from localStorage
            Object.keys(mappedErrors).forEach(field => {
              // Don't clear email error if it's a server error from email availability check
              if (field !== 'email' || !emailError.value) {
                formBuilder.value.setFieldError(field, undefined)
              }
            })
          }
        }, 3000)
      }
    }, 100)
  }

  hasLoaded.value = true
})

watch(model, (newValue) => {
  if (hasLoaded.value && newValue) {
    localStorage.setItem('contactInfo', JSON.stringify(newValue))
  }
}, { deep: true })

// Re-apply email error whenever it exists (to handle UiFormBuilder clearing it on blur)
watch(emailError, (error) => {
  if (error && formBuilder.value && formBuilder.value.setFieldError) {
    // Use nextTick to ensure form is ready
    setTimeout(() => {
      if (formBuilder.value && formBuilder.value.setFieldError) {
        formBuilder.value.setFieldError('email', error)
      }
    }, 0)
  }
}, { immediate: true })

function nextStep() {
  // Check if there are any server errors (including email availability)
  if (Object.keys(serverErrors.value).length > 0) {
    return
  }

  const result = contactInfoSchema.value.safeParse(model.value)

  if (!result.success) {
    return
  }

  if (model.value) {
    localStorage.setItem('contactInfo', JSON.stringify(model.value))
  }
  router.push('/expert/signup/professional-details')
}

const back = () => {
  router.push('/expert/signup')
}

</script>

<template>
  <StepPanel>
    <div class="flex flex-col gap-8">
      <div class="flex flex-col gap-4">
        <div class="flex flex-row items-center justify-between">
          <UiText
            as="p"
            class="font-normal font-archivo text-primary tracking-10p"
            :text="listingType === 'freelancer' ? 'CREATE FREELANCER LISTING' : (listingType === 'app_service_provider' ? 'CREATE APP SERVICE PARTNER LISTING' : 'CREATE AGENCY LISTING')"
          />
          <UiText as="p" class="font-normal font-archivo text-primary tracking-10p" text="1/3" />
        </div>
        <UiText as="h3" class="font-normal text-primary" text="Contact Information" />
        <UiText
          as="p"
          class="text-primary"
          text="Let’s start with the information leads can use to contact you. The email you enter here will be connected to your expert account."
        />
      </div>

      <div class="flex flex-col gap-8">
        <UiFormBuilder ref="formBuilder" :schema="contactInfoSchema" :rows="rows" :initialValues="initialValues" :server-errors="serverErrors" scroll-to-first-error v-model="model" @submit="nextStep">
          <template #actions>
            <div class="flex justify-between gap-4">
              <UiButton
                  variant="outline"
                  label="Back"
                  @click="back"
              />
              <UiButton
                  variant="primary"
                  label="Next"
                  type="submit"
              />
            </div>
          </template>
        </UiFormBuilder>
      </div>
    </div>
  </StepPanel>
</template>
